import { useState } from 'react'
import { Mail, Trash2, Users, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Role } from '../api/auth'
import {
  useCreateInvite,
  useDeleteMembership,
  useListInvites,
  useMe,
  useRevokeInvite,
  useUpdateMembership,
} from '../api/auth'

const ROLE_OPTIONS: Role[] = ['owner', 'admin', 'member', 'viewer']

export function MembersPage() {
  const { t } = useTranslation('auth')
  const { data: me } = useMe()
  const activeOrg = me?.memberships.find((m) => m.org_id === me.active_org_id)
  const myRole = activeOrg?.role ?? 'member'
  const orgId = me?.active_org_id ?? ''
  const canManage = myRole === 'owner' || myRole === 'admin'

  const createInvite = useCreateInvite(orgId)
  const updateMembership = useUpdateMembership(orgId)
  const deleteMembership = useDeleteMembership(orgId)
  const revokeInvite = useRevokeInvite(orgId)
  const invitesQuery = useListInvites(orgId, canManage)

  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('member')
  const [lastInviteToken, setLastInviteToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onInvite(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLastInviteToken(null)
    try {
      const res = await createInvite.mutateAsync({
        email: email.trim().toLowerCase(),
        role: inviteRole,
      })
      setLastInviteToken(res.token ?? null)
      setEmail('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function onChangeRole(userId: string, role: Role) {
    setError(null)
    try {
      await updateMembership.mutateAsync({ userId, role })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function onRemoveMember(userId: string) {
    if (!window.confirm(t('members.removeConfirm'))) return
    setError(null)
    try {
      await deleteMembership.mutateAsync(userId)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function onRevokeInvite(token: string) {
    if (!window.confirm(t('members.revokeInviteConfirm'))) return
    setError(null)
    try {
      await revokeInvite.mutateAsync(token)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (!me) return <div className="p-8 text-text-secondary">{t('common.loading')}</div>
  if (!activeOrg) return <div className="p-8 text-text-secondary">{t('common.noActiveOrg')}</div>

  const roleLabel = (role: string) =>
    t(`members.roles.${role}`, { defaultValue: role })

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('members.title', { org: activeOrg.org?.name })}</h1>
        <p className="text-sm text-text-secondary">
          {t('members.subtitle')}
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-error/40 bg-error/10 p-3 text-sm text-error">
          {error}
        </div>
      )}

      {canManage ? (
        <form onSubmit={onInvite} className="space-y-3 rounded-lg border border-border bg-surface p-4">
          <h2 className="font-medium">{t('members.inviteByEmail')}</h2>
          <div className="flex flex-wrap gap-2">
            <input
              type="email"
              required
              className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('members.emailPlaceholder')}
            />
            <select
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              aria-label={t('members.roleLabel')}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={createInvite.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              <Mail className="h-4 w-4" /> {t('members.inviteCta')}
            </button>
          </div>
          {lastInviteToken && (
            <div className="rounded-lg border border-success/40 bg-success/10 p-3 text-sm">
              {t('members.inviteCreated')}
              <code className="mt-2 block break-all rounded bg-background p-2 font-mono text-xs">
                {`${window.location.origin}/invites/${lastInviteToken}`}
              </code>
              <p className="mt-2 text-xs text-text-secondary">
                {t('members.inviteHint')}
              </p>
            </div>
          )}
        </form>
      ) : (
        <div className="rounded-lg border border-border bg-surface p-4 text-sm text-text-secondary">
          <Users className="inline h-4 w-4 align-text-bottom" /> {t('members.onlyAdminsInvite')}
        </div>
      )}

      <section className="rounded-lg border border-border bg-surface">
        <header className="border-b border-border px-4 py-3 font-medium">
          {t('members.membersTable')}
        </header>
        <div className="flex items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <div className="truncate font-medium">
              {me.user.email} <span className="text-xs text-text-secondary">{t('members.selfRow')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManage ? (
              <select
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm disabled:opacity-50"
                value={activeOrg.role}
                disabled={updateMembership.isPending}
                onChange={(e) => onChangeRole(me.user.id, e.target.value as Role)}
                aria-label={t('members.roleLabel')}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            ) : (
              <span className="rounded-full bg-surface-elevated px-3 py-1 text-xs uppercase">
                {roleLabel(activeOrg.role)}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemoveMember(me.user.id)}
              disabled={deleteMembership.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-text-secondary hover:bg-surface-elevated disabled:opacity-50"
              aria-label={t('members.remove')}
            >
              <Trash2 className="h-3.5 w-3.5" /> {t('members.remove')}
            </button>
          </div>
        </div>
        <p className="border-t border-border p-3 text-xs text-text-secondary">
          {t('members.listingNote')}
        </p>
      </section>

      {canManage && (
        <section className="rounded-lg border border-border bg-surface">
          <header className="border-b border-border px-4 py-3 font-medium">
            {t('members.pendingInvites')}
          </header>
          {invitesQuery.isLoading ? (
            <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>
          ) : invitesQuery.data && invitesQuery.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {invitesQuery.data.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{inv.email}</div>
                    <div className="text-xs text-text-secondary">
                      {roleLabel(inv.role)} ·{' '}
                      {t('members.expiresOn', {
                        date: new Date(inv.expires_at * 1000).toLocaleDateString(),
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRevokeInvite(inv.token)}
                    disabled={revokeInvite.isPending}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-text-secondary hover:bg-surface-elevated disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" /> {t('members.revoke')}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-sm text-text-secondary">{t('members.noPendingInvites')}</div>
          )}
        </section>
      )}
    </div>
  )
}
